import { Vector3 } from 'three';

import type { Skeleton } from '../../skeleton/core';
import {
  EXPECTED_CHILD_COUNTS,
  EXPECTED_DEPTHS,
  isHeadLikeName,
  isLegLikeName,
  isNeckLikeName,
  isPelvisLikeName,
  isSpineLikeName,
} from '../../schema/mapping';
import {
  type ScoreBreakdown,
  WEIGHTS,
  symmetryScore,
  depthScore,
  lengthRatioScore,
  namingScore,
  topologyScore,
  totalScore,
} from '../scoring';
import type { SkeletonSpatialData } from '../spatial';
import type { RecognizerConfig } from '../config';

// ─── Inlined chain utilities ─────────────────────────────────────────────

function extractPathsToLeaves(
  skel: Skeleton,
  startIdx: number,
  ignoreAux = true,
): number[][] {
  const paths: number[][] = [];

  function getEffectiveChildren(idx: number): number[] {
    const bone = skel.bone(idx);
    const result: number[] = [];
    for (const c of bone.children) {
      if (ignoreAux && skel.bone(c).auxiliary) {
        result.push(...getEffectiveChildren(c));
      } else {
        result.push(c);
      }
    }
    return result;
  }

  function dfs(idx: number, current: number[]): void {
    const bone = skel.bone(idx);
    if (ignoreAux && bone.auxiliary) return;
    current.push(idx);
    const children = getEffectiveChildren(idx);
    if (children.length === 0) {
      paths.push([...current]);
    } else {
      for (const child of children) {
        dfs(child, current);
      }
    }
    current.pop();
  }

  dfs(startIdx, []);
  return paths;
}

function chainLength(skel: Skeleton, chain: number[]): number {
  if (chain.length < 2) return 0.0;
  let length = 0.0;
  for (const idx of chain) {
    length += skel.bone(idx).length;
  }
  return length;
}

function chainDirection(skel: Skeleton, chain: number[]): Vector3 {
  if (chain.length === 0) return new Vector3();
  const sum = new Vector3();
  let count = 0;
  for (const idx of chain) {
    const b = skel.bone(idx);
    if (b.length > 0) {
      sum.add(b.direction);
      count++;
    }
  }
  if (count === 0) return new Vector3();
  sum.divideScalar(count);
  const norm = sum.length();
  if (norm < 1e-8) return new Vector3();
  return sum.divideScalar(norm);
}

function bestTwoSymmetricChains(
  candidateChains: number[][],
  skel: Skeleton,
  axisVec: Vector3,
): [number[], number[], number] {
  let bestScore = -1.0;
  let bestPair: [number[], number[]] = [[], []];
  const axisLen = axisVec.length();
  const axis =
    axisLen > 1e-8 ? axisVec.clone().divideScalar(axisLen) : axisVec.clone();

  for (let i = 0; i < candidateChains.length; i++) {
    const chainA = candidateChains[i];
    for (let j = i + 1; j < candidateChains.length; j++) {
      const chainB = candidateChains[j];
      const lenA = chainLength(skel, chainA);
      const lenB = chainLength(skel, chainB);
      const dirA = chainDirection(skel, chainA);
      const dirB = chainDirection(skel, chainB);
      const mirrorDot = dirA.dot(dirB.clone().negate());
      const depthA = skel.bone(chainA[0]).depth;
      const depthB = skel.bone(chainB[0]).depth;
      const score = symmetryScore(lenA, lenB, depthA, depthB, mirrorDot);

      if (score > bestScore) {
        bestScore = score;
        bestPair = [[...chainA], [...chainB]];
      }
    }
  }
  return [bestPair[0], bestPair[1], bestScore];
}

// ─── Spatial leg-like detection ──────────────────────────────────────────

function isLegLikeSpatial(
  skel: Skeleton,
  path: number[],
  spatialData: SkeletonSpatialData,
  pelvisVertical: number,
): boolean {
  if (path.length < 2) return false;

  const startVert = spatialData.getVerticalPosition(path[0]);
  const endVert = spatialData.getVerticalPosition(path[path.length - 1]);

  if (endVert >= startVert) return false;
  if (endVert > 0.35) return false;
  if (startVert > pelvisVertical + 0.1) return false;
  if (path.length < 2 || path.length > 8) return false;

  let totalLen = 0;
  for (const idx of path) {
    totalLen += skel.bone(idx).length;
  }
  const skelH = spatialData.totalHeight > 1e-6 ? spatialData.totalHeight : 100.0;
  if (totalLen < skelH * 0.05) return false;

  const verticalDrop = startVert - endVert;
  if (verticalDrop < 0.2) return false;

  return true;
}

function isLegLikeNaming(path: number[], skel: Skeleton): boolean {
  const limit = Math.min(path.length, 5);
  for (let i = 0; i < limit; i++) {
    const name = skel.bone(path[i]).name;
    if (isLegLikeName(name) || isPelvisLikeName(name)) return true;
  }
  return false;
}

// ─── Spatial pelvis candidates ───────────────────────────────────────────

function findStructuralPelvisAncestors(
  skel: Skeleton,
  functionalPelvisIdx: number,
  spatialData: SkeletonSpatialData,
): [number, number][] {
  const candidates: [number, number][] = [];
  let currentIdx = functionalPelvisIdx;
  let depth = 0;
  const maxDepth = 5;

  while (depth < maxDepth) {
    const bone = skel.bone(currentIdx);
    if (bone.parent === null || bone.parent < 0) break;

    const parentIdx = bone.parent;
    const parent = skel.bone(parentIdx);

    if (parent.auxiliary) {
      currentIdx = parentIdx;
      depth++;
      continue;
    }

    const parentVert = spatialData.getVerticalPosition(parentIdx);
    const parentLat = Math.abs(spatialData.getLateralOffset(parentIdx));

    if (parentLat > 0.30) {
      currentIdx = parentIdx;
      depth++;
      continue;
    }

    if (parentVert < 0.25 || parentVert > 0.60) {
      currentIdx = parentIdx;
      depth++;
      continue;
    }

    let hasUpwardBranch = false;
    for (const childIdx of parent.children) {
      if (childIdx === currentIdx) continue;
      const child = skel.bone(childIdx);
      if (child.auxiliary) continue;

      const childVert = spatialData.getVerticalPosition(childIdx);
      if (childVert > parentVert + 0.05) {
        const childLat = Math.abs(spatialData.getLateralOffset(childIdx));
        if (childLat < 0.25) {
          hasUpwardBranch = true;
          break;
        }
      }
    }

    if (hasUpwardBranch) {
      let score = 3.0;
      if (parentVert >= 0.35 && parentVert <= 0.50) score += 0.5;
      if (parentLat < 0.15) score += 0.5;
      score -= depth * 0.1;
      candidates.push([score, parentIdx]);
    }

    currentIdx = parentIdx;
    depth++;
  }

  return candidates;
}

function findSpatialPelvisCandidates(
  skel: Skeleton,
  spatialData: SkeletonSpatialData,
): number[] {
  const candidates: [number, number][] = [];
  const functionalPelvisCandidates: number[] = [];

  for (const [boneIdx, bp] of spatialData.branchingPoints) {
    const bone = skel.bone(boneIdx);

    if (bone.hasSide) continue;
    if (!bp.hasSymmetricChildren) continue;
    if (!bp.hasDownwardChildren) continue;

    functionalPelvisCandidates.push(boneIdx);

    let hasUpward = bp.hasUpwardChildren;
    if (!hasUpward && bone.parent !== null && bone.parent >= 0) {
      const parent = skel.bone(bone.parent);
      for (const siblingIdx of parent.children) {
        if (siblingIdx === boneIdx) continue;
        const sibling = skel.bone(siblingIdx);
        if (sibling.auxiliary) continue;

        const siblingVert = spatialData.getVerticalPosition(siblingIdx);
        if (siblingVert > bp.verticalPosition + 0.02) {
          hasUpward = true;
          break;
        }

        const skelH = spatialData.totalHeight > 1e-6 ? spatialData.totalHeight : 100.0;
        if (sibling.length > skelH * 0.01) {
          const dirNorm = sibling.direction.length();
          if (dirNorm > 0.01) {
            const normalizedDir = sibling.direction.clone().divideScalar(dirNorm);
            const upAxis = new Vector3(0.0, 1.0, 0.0);
            const siblingUpDot = normalizedDir.dot(upAxis);
            if (siblingUpDot > 0.5) {
              hasUpward = true;
              break;
            }
          }
        }
      }
    }

    const pelvisVertical = spatialData.getVerticalPosition(boneIdx);
    const childPaths: number[][] = [];
    for (const childIdx of bone.children) {
      if (skel.bone(childIdx).auxiliary) continue;
      childPaths.push(...extractPathsToLeaves(skel, childIdx, true));
    }
    const spatialLegLikePaths = childPaths.filter((path) =>
      isLegLikeSpatial(skel, path, spatialData, pelvisVertical),
    );
    const inStandardPelvisBand =
      bp.verticalPosition >= 0.15 && bp.verticalPosition <= 0.65;
    const isHighLongLegPelvis =
      bp.verticalPosition > 0.65 &&
      bp.verticalPosition <= 0.85 &&
      hasUpward &&
      spatialLegLikePaths.length >= 2;

    if (!inStandardPelvisBand && !isHighLongLegPelvis) continue;
    if (Math.abs(bp.lateralOffset) > 0.35) continue;

    let score = 0.0;
    score += bp.symmetryScore * 2.0;
    if (hasUpward) score += 1.0;
    if (bp.childCount === 3 || bp.childCount === 4) score += 1.0;
    else if (bp.childCount === 2) score += 0.5;
    if (bp.verticalPosition >= 0.25 && bp.verticalPosition <= 0.55) score += 0.5;
    if (isHighLongLegPelvis) score += 0.4;

    candidates.push([score, boneIdx]);
  }

  for (const funcPelvisIdx of functionalPelvisCandidates) {
    const ancestorCandidates = findStructuralPelvisAncestors(
      skel,
      funcPelvisIdx,
      spatialData,
    );
    const existingIndices = new Set(candidates.map((c) => c[1]));
    for (const [score, ancestorIdx] of ancestorCandidates) {
      if (!existingIndices.has(ancestorIdx)) {
        candidates.push([score, ancestorIdx]);
      }
    }
  }

  candidates.sort((a, b) => b[0] - a[0]);
  return candidates.map(([, idx]) => idx);
}

// ─── Main pelvis detection ───────────────────────────────────────────────

export function pickBestPelvis(
  skel: Skeleton,
  config: RecognizerConfig,
  spatialData: SkeletonSpatialData | null = null,
): [number, ScoreBreakdown, number[][]] {
  let bestIdx = skel.root;
  let bestScore = -1.0;
  let bestBreakdown: ScoreBreakdown = {
    topology: 0,
    symmetry: 0,
    direction: 0,
    lengthRatio: 0,
    naming: 0,
    depth: 0,
    total: 0,
  };
  let bestLegChains: number[][] = [];

  const trustSpatial = config.shouldTrustSpatialPelvis();

  let spatialCandidates = new Set<number>();
  if (spatialData !== null) {
    spatialCandidates = new Set(findSpatialPelvisCandidates(skel, spatialData));
  }

  for (const bone of skel.bones) {
    if (bone.auxiliary) continue;
    const nameLower = bone.name.toLowerCase();
    const pelvisNamed = isPelvisLikeName(nameLower);

    const isSpatialCandidate = spatialCandidates.has(bone.index);

    if (bone.hasSide) continue;

    if (!(trustSpatial && isSpatialCandidate)) {
      if (isLegLikeName(nameLower) && !pelvisNamed) continue;
      if (isHeadLikeName(nameLower) || isNeckLikeName(nameLower)) continue;
    }

    const nonAuxChildren = bone.children.filter(
      (c) => !skel.bone(c).auxiliary,
    );

    if (nonAuxChildren.length < 2) {
      if (!pelvisNamed && !(trustSpatial && isSpatialCandidate)) continue;
    }

    const paths: number[][] = [];
    for (const child of nonAuxChildren) {
      paths.push(...extractPathsToLeaves(skel, child, true));
    }
    if (paths.length < 2) continue;

    const pelvisVertical = spatialData
      ? spatialData.getVerticalPosition(bone.index)
      : 0.5;

    const legLikePathsNaming = paths.filter((p) => isLegLikeNaming(p, skel));

    let legLikePathsSpatial: number[][] = [];
    if (legLikePathsNaming.length < 2 && spatialData !== null) {
      legLikePathsSpatial = paths.filter((p) =>
        isLegLikeSpatial(skel, p, spatialData, pelvisVertical),
      );
    }

    let legLikePaths: number[][];
    if (legLikePathsNaming.length >= 2) {
      legLikePaths = legLikePathsNaming;
    } else if (legLikePathsSpatial.length >= 2) {
      legLikePaths = legLikePathsSpatial;
    } else {
      legLikePaths = [...legLikePathsNaming, ...legLikePathsSpatial];
    }

    const candidatePaths =
      legLikePaths.length >= 2 ? legLikePaths : paths;

    const hasNamingEvidence =
      pelvisNamed || legLikePathsNaming.length >= 2;
    const hasSpatialEvidence =
      isSpatialCandidate && legLikePathsSpatial.length >= 2;

    if (!hasNamingEvidence && !hasSpatialEvidence) {
      if (!(trustSpatial && isSpatialCandidate)) continue;
    }

    let directLegChildren = 0;
    let directLegChildrenSpatial = 0;
    let hasLegDescendant = false;
    let hasSpineChild = false;
    let hasSpineSibling = false;
    let hasUpwardChild = false;

    for (const childIdx of nonAuxChildren) {
      const childName = skel.bone(childIdx).name.toLowerCase();

      if (isLegLikeName(childName) || isPelvisLikeName(childName)) {
        directLegChildren++;
      } else {
        for (const gcIdx of skel.bone(childIdx).children) {
          const gcName = skel.bone(gcIdx).name.toLowerCase();
          if (isLegLikeName(gcName) || isPelvisLikeName(gcName)) {
            hasLegDescendant = true;
            break;
          }
        }
      }

      if (spatialData !== null) {
        const child = skel.bone(childIdx);
        const childVert = spatialData.getVerticalPosition(childIdx);

        const positionGoesDown = childVert < pelvisVertical - 0.05;

        let directionGoesDown = false;
        const skelHeight =
          spatialData.totalHeight > 1e-6 ? spatialData.totalHeight : 100.0;
        const minBoneLen = skelHeight * 0.01;
        const minDrop = skelHeight * 0.05;
        if (child.length > minBoneLen) {
          const childDirY = child.direction.y * child.length;
          if (childDirY < -minDrop) directionGoesDown = true;
        }

        if (positionGoesDown || directionGoesDown) {
          directLegChildrenSpatial++;
        } else if (childVert > pelvisVertical + 0.05) {
          hasUpwardChild = true;
        } else if (child.length > minBoneLen) {
          const childDirY = child.direction.y * child.length;
          if (childDirY > minDrop) hasUpwardChild = true;
        }
      }

      if (isSpineLikeName(childName)) {
        hasSpineChild = true;
      }
    }

    if (directLegChildren < 2 && directLegChildrenSpatial >= 2 && trustSpatial) {
      directLegChildren = directLegChildrenSpatial;
    }

    if (!hasSpineChild && hasUpwardChild && trustSpatial) {
      hasSpineChild = true;
    }

    if (bone.parent !== null && bone.parent >= 0) {
      const parent = skel.bone(bone.parent);
      for (const siblingIdx of parent.children) {
        if (siblingIdx === bone.index) continue;
        const siblingName = skel.bone(siblingIdx).name.toLowerCase();
        if (isSpineLikeName(siblingName)) {
          hasSpineSibling = true;
          break;
        }
        if (spatialData !== null && trustSpatial) {
          const siblingVert = spatialData.getVerticalPosition(siblingIdx);
          if (siblingVert > pelvisVertical + 0.1) {
            hasSpineSibling = true;
            break;
          }
        }
      }
    }

    const [chainA, chainB, symmetry] = bestTwoSymmetricChains(
      candidatePaths,
      skel,
      new Vector3(1.0, 0.0, 0.0),
    );

    const topology = topologyScore(
      bone.parent !== null || bone.depth === 0,
      EXPECTED_CHILD_COUNTS['Pelvis'] ?? [2, 4],
      nonAuxChildren.length,
      false,
    );
    const naming = namingScore(
      bone.name,
      config.namingHints['Pelvis'] ?? ['pelvis', 'hip'],
    );
    const rootLike = namingScore(
      bone.name,
      config.namingHints['Root'] ?? ['root'],
    );
    const structuralRootLikePelvis =
      rootLike >= 0.2 &&
      naming < 0.1 &&
      directLegChildren >= 2 &&
      hasSpineChild;

    if (rootLike >= 0.2 && naming < 0.1) {
      if (!structuralRootLikePelvis && !(trustSpatial && isSpatialCandidate)) continue;
    }

    if (legLikePaths.length < 2 && naming < 0.1) {
      if (!(trustSpatial && isSpatialCandidate)) continue;
    }

    const depth = depthScore(bone.depth, EXPECTED_DEPTHS['Pelvis'] ?? [1, 2]);
    const lenRatio = lengthRatioScore(
      chainLength(skel, chainA),
      chainLength(skel, chainB),
    );

    const breakdown: ScoreBreakdown = {
      topology,
      symmetry,
      direction: 0.5,
      lengthRatio: lenRatio,
      naming,
      depth,
      total: 0.0,
    };
    breakdown.total = totalScore(breakdown, config.weights && Object.keys(config.weights).length > 0 ? config.weights : WEIGHTS);

    if (pelvisNamed) breakdown.total += 0.08;
    if (
      nameLower.includes('spine') ||
      nameLower.includes('espina') ||
      nameLower.includes('colonne') ||
      nameLower.includes('vertébrale') ||
      nameLower.includes('vertebrale')
    ) breakdown.total -= 0.15;

    if (directLegChildren >= 2) {
      breakdown.total += 0.10;
    } else if (directLegChildren === 1) {
      breakdown.total += 0.03;
    } else if (hasLegDescendant && pelvisNamed) {
      breakdown.total += 0.05;
    }

    if (hasSpineChild && (hasLegDescendant || directLegChildren > 0)) {
      breakdown.total += 0.15;
    } else if (hasSpineSibling && hasSpineChild) {
      breakdown.total += 0.08;
    } else if (hasSpineSibling && !hasSpineChild) {
      breakdown.total -= 0.08;
    }

    if (spatialCandidates.has(bone.index)) {
      const bp = spatialData
        ? spatialData.getBranchingPoint(bone.index)
        : null;
      if (bp !== null) {
        if (trustSpatial) {
          const spatialBoost = 0.15 + bp.symmetryScore * 0.15;
          breakdown.total += spatialBoost;
          if (legLikePathsSpatial.length >= 2) breakdown.total += 0.10;
        } else {
          const spatialBoost = 0.05 + bp.symmetryScore * 0.10;
          breakdown.total += spatialBoost;
        }

        if (bp.verticalPosition >= 0.25 && bp.verticalPosition <= 0.45) {
          breakdown.total += 0.08;
        } else if (
          bp.verticalPosition >= 0.2 &&
          bp.verticalPosition <= 0.5
        ) {
          breakdown.total += 0.04;
        } else if (bp.verticalPosition > 0.5) {
          breakdown.total -= 0.05;
        } else if (bp.verticalPosition > 0.7) {
          breakdown.total -= 0.10;
        }
      }
    }

    if (breakdown.total > bestScore) {
      bestScore = breakdown.total;
      bestIdx = bone.index;
      bestBreakdown = breakdown;
      bestLegChains = [chainA, chainB];
    }
  }

  return [bestIdx, bestBreakdown, bestLegChains];
}
